/* Gestão dos documentos legais versionados (ADR-0774). Esta é a superfície que permite a REVISÃO
   JURÍDICA acontecer: sem ela, o rascunho estrutural semeado na v1 fica no ar para sempre, porque não
   existe outro caminho para publicar uma v2 revisada.

   READ é dos 4 tiers (quem atende usuário precisa poder ler o texto vigente); MANAGE é `super_admin`
   APENAS — mais restrito que o FAQ de propósito, porque publicar termos é assumir obrigação
   contratual perante todos os usuários, não editar conteúdo de ajuda.

   Não há filtros nem paginação: o histórico de um documento legal tem poucas versões por natureza, e
   inventar busca aqui só esconderia o que importa (qual versão está EM VIGOR). */
import type { LegalDocKey, StaffRole } from '@planuze/admin-contracts';
import { LEGAL_BULK_MAX_VERSIONS, LEGAL_DOC_KEYS } from '@planuze/admin-contracts';
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useMemo, useState } from 'react';
import { SegmentedControl, type SegmentOption } from '../components/filters/segmented-control.js';
import { LegalBulkBar } from '../components/legal/legal-bulk-bar.js';
import { legalErrorMessage } from '../components/legal/legal-error.js';
import { LegalPublishDialog } from '../components/legal/legal-publish-dialog.js';
import { LegalVersionFormModal } from '../components/legal/legal-version-form-modal.js';
import { LegalVersionsTable } from '../components/legal/legal-versions-table.js';
import { useLegalContent } from '../content/sections/legal.js';
import {
  useBulkDeleteLegalDrafts,
  useDeleteLegalDraft,
  useLegalVersions,
  usePublishLegalVersion,
} from '../hooks/use-legal.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { groupLegalVersions, type LegalVersionGroup } from '../lib/legal-versions.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin'];

/** Estado do modal de versão: ver/editar uma existente, ou criar (opcionalmente copiando uma). */
type FormState =
  | { readonly kind: 'closed' }
  | { readonly kind: 'open'; readonly group?: LegalVersionGroup; readonly copyFrom?: number };

export function LegalPage() {
  const content = useLegalContent();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const [docKey, setDocKey] = useState<LegalDocKey>('terms');
  const { data, isLoading, isError } = useLegalVersions(docKey);
  const publish = usePublishLegalVersion(docKey);
  const remove = useDeleteLegalDraft(docKey);
  const bulkRemove = useBulkDeleteLegalDrafts(docKey);

  const [form, setForm] = useState<FormState>({ kind: 'closed' });
  const [toPublish, setToPublish] = useState<LegalVersionGroup | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<LegalVersionGroup | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingBulkIds, setPendingBulkIds] = useState<readonly string[] | null>(null);

  /* A vigência é relativa ao AGORA: recalcula a cada resposta do servidor. Um `setInterval` só para
     virar "programada" em "em vigor" no relógio do navegador seria precisão falsa — quem decide o
     que está vigente na leitura pública é o servidor. */
  const groups = useMemo(() => groupLegalVersions(data ?? [], new Date()), [data]);
  const draftIds = useMemo(
    () =>
      groups.flatMap((group) => {
        const id = group.status === 'draft' ? group.locales[0]?.id : undefined;
        return id === undefined ? [] : [id];
      }),
    [groups],
  );
  const effectiveSelected = new Set(draftIds.filter((id) => selectedIds.has(id)));
  const toggle = (id: string): void =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < LEGAL_BULK_MAX_VERSIONS) next.add(id);
      return next;
    });
  const toggleAll = (checked: boolean): void =>
    setSelectedIds(checked ? new Set(draftIds.slice(0, LEGAL_BULK_MAX_VERSIONS)) : new Set());

  const docOptions: readonly SegmentOption<LegalDocKey>[] = LEGAL_DOC_KEYS.map((key) => ({
    value: key,
    label: content.docLabels[key],
  }));

  const closeForm = (): void => setForm({ kind: 'closed' });
  const closePublish = (): void => {
    setToPublish(null);
    setPublishError(null);
  };

  const onConfirmPublish = (effectiveAt: Date | undefined): void => {
    const group = toPublish;
    if (group === null) return;
    const row = group.locales[0];
    if (row === undefined) return;
    setPublishError(null);
    publish.mutate(
      { id: row.id, ...(effectiveAt !== undefined && { effectiveAt }) },
      {
        onSuccess: () => {
          toast.success(
            effectiveAt === undefined
              ? content.toastPublishedNow(group.version)
              : content.toastPublishedScheduled(group.version),
          );
          closePublish();
        },
        onError: (err) => setPublishError(legalErrorMessage(err)),
      },
    );
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{content.pageTitle}</PageTitle>
          <PageDescription>{content.pageDescription}</PageDescription>
        </PageTitleBlock>
        {canManage && (
          <PageActions>
            <Button onClick={() => setForm({ kind: 'open' })}>{content.newVersionButton}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <InlineAlert variant="info">
          {content.infoVersionNumberIntro}
          <strong>{content.infoVersionNumberStrong}</strong>
          {content.infoAfterVersionNumber}
          <strong>{content.infoPublishedImmutableStrong}</strong>
          {content.infoAfterPublishedImmutable}
          <strong>{content.infoEffectiveAtStrong}</strong>
          {content.infoBetweenEffectiveAndPublished}
          <strong>{content.infoPublishedAtStrong}</strong>
          {content.infoAfterPublishedAt}
          <em>{content.infoSameVersionEm}</em>
          {content.infoAfterSameVersion}
          <strong>{content.infoSuperAdminStrong}</strong>
          {content.infoAfterSuperAdmin}
        </InlineAlert>
        <SegmentedControl
          value={docKey}
          onChange={(next) => {
            setDocKey(next);
            setSelectedIds(new Set());
          }}
          options={docOptions}
          ariaLabel={content.docPickerAriaLabel}
        />
        {canManage && (
          <LegalBulkBar
            selectedCount={effectiveSelected.size}
            busy={bulkRemove.isPending}
            onRemove={() => setPendingBulkIds([...effectiveSelected])}
            onClear={() => setSelectedIds(new Set())}
          />
        )}
        {isError ? (
          <InlineAlert variant="destructive">{content.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 8 : 7} />
        ) : (
          <LegalVersionsTable
            groups={groups}
            canManage={canManage}
            busy={publish.isPending || remove.isPending || bulkRemove.isPending}
            onView={(group) => setForm({ kind: 'open', group })}
            onEdit={(group) => setForm({ kind: 'open', group })}
            onCopy={(group) => setForm({ kind: 'open', copyFrom: group.version })}
            onPublish={setToPublish}
            onDelete={setToDelete}
            onCreate={() => setForm({ kind: 'open' })}
            selectedIds={effectiveSelected}
            onToggle={toggle}
            onToggleAll={toggleAll}
          />
        )}
      </PageContent>
      {form.kind === 'open' && (
        <LegalVersionFormModal
          open
          onClose={closeForm}
          docKey={docKey}
          groups={groups}
          group={form.group}
          copyFromVersion={form.copyFrom}
          canManage={canManage}
        />
      )}
      {toPublish !== null && (
        <LegalPublishDialog
          open
          onClose={closePublish}
          group={toPublish}
          docLabel={content.docLabels[docKey]}
          pending={publish.isPending}
          error={publishError}
          onConfirm={onConfirmPublish}
        />
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title={content.removeDraftTitle}
        description={
          toDelete === null ? undefined : content.removeDraftDescription(toDelete.version)
        }
        confirmLabel={content.removeConfirmLabel}
        variant="destructive"
        mapError={legalErrorMessage}
        onConfirm={async () => {
          const group = toDelete;
          if (group === null) return;
          await remove.mutateAsync(group.locales.map((row) => row.id));
          toast.success(content.toastDraftRemoved(group.version));
          setToDelete(null);
        }}
      />
      <ConfirmDialog
        open={pendingBulkIds !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulkIds(null);
        }}
        title={content.bulk.title}
        description={content.bulk.description(pendingBulkIds?.length ?? 0)}
        confirmLabel={content.bulk.remove}
        variant="destructive"
        mapError={legalErrorMessage}
        onConfirm={async () => {
          if (pendingBulkIds === null || pendingBulkIds.length === 0) return;
          const response = await bulkRemove.mutateAsync(pendingBulkIds);
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) toast.success(content.bulk.success(response.results.length));
          else {
            toast.warning(
              content.bulk.partial(response.results.length - failed.length, failed.length),
            );
          }
          setSelectedIds(new Set(failed.map((result) => result.id)));
          setPendingBulkIds(null);
        }}
      />
    </Page>
  );
}
